import Image from 'next/image';
import React, { useEffect, useState } from 'react';
import { Components } from 'react-markdown/lib/ast-to-react';
import { atomOneDark, atomOneLight } from 'react-syntax-highlighter/dist/cjs/styles/hljs';
import { LightAsync as SyntaxHighlighter, SyntaxHighlighterProps } from 'react-syntax-highlighter';
import { useTheme } from 'next-themes';
import slugify from '@sindresorhus/slugify';
import { FiCopy, FiCheck } from 'react-icons/fi';

const id_replacements: [string, string][] = [['.', ''], ["'", '']]

export const mdxComponents: Components = {
	h1: ({ children }) => {
		const id = slugify(children?.toString(), {
			customReplacements: id_replacements
		});
		return <h1 id={id} className="headline text-4xl mt-8">{children}</h1>;
	},
	h2: ({ children }) => {
		const id = slugify(children?.toString(), {
			customReplacements: id_replacements
		});
		return <h2 id={id} className="headline text-3xl mt-6">{children}</h2>;
	},
	h3: ({ children }) => {
		const id = slugify(children?.toString(), {
			customReplacements: id_replacements
		});
		return <h3 id={id} className="headline text-2xl mt-4">{children}</h3>;
	},
	h4: ({ children }) => {
		const id = slugify(children?.toString(), {
			customReplacements: id_replacements
		});
		return <h4 id={id} className="headline text-xl mt-4">{children}</h4>;
	},
	a: ({ children, href }) => {
		const isInternalLink = href && (href.startsWith('/') || href.startsWith('#'));
		return (
			<a
				href={href}
				{...(isInternalLink
					? {} // No need to add `target` or `rel` for internal links
					: { target: "_blank", rel: "noopener noreferrer" } // External links open in a new tab
				)}
				className="relative underlined text-yellow-600"
			>
				{children}
			</a>
		);
	},
	ul: ({ children }) => <ul className="list-disc ml-4 my-2">{children}</ul>,
	ol: ({ children }) => <ol className="list-decimal ml-4 my-2">{children}</ol>,
	li: ({ children }) => <li className="my-1">{children}</li>,
	hr: ({ children }) => <hr className="mt-6">{children}</hr>,
	p: ({ children }) => {
		// return <p className="mt-4 mb-4 text-lg leading-7">{children}</p>
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const isImage = children[0]?.type?.name === 'img';
		return isImage ? <div className='my-2'>{children}</div> : <p className="my-3 text-lg leading-7">{children}</p>
	},
	code: ({ node, inline, className, children, ...props }) => {
		const language = className?.replace('language-', '') || 'python';
		return (
			<CustomCodeHighlighter
				inline={inline}
				language={language}
			>
				{children}
			</CustomCodeHighlighter>)
	},
	img: ({ alt, src }) => {
		/* eslint-disable */
		const [aspectRatio, setAspectRatio] = useState(16 / 9);
		const [isLoading, setIsLoading] = useState(true);

		const handleImageLoad = ({ naturalWidth, naturalHeight }: { naturalWidth: any, naturalHeight: any }) => {
			console.log("naturalDimensions", naturalWidth, naturalHeight)
			setAspectRatio(naturalWidth / naturalHeight);
			setIsLoading(false);
		};
		/* eslint-enable */

		return (
			<div className="w-full relative" style={{ paddingBottom: `${100 / aspectRatio}%` }}>
				{isLoading && (
					<div className="absolute inset-0 flex items-center justify-center bg-gray-200">
						<div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
					</div>
				)}
				<Image
					src={src!}
					alt={alt}
					layout="fill"
					objectFit="contain"
					className="my-4"
					onLoadingComplete={handleImageLoad}
					placeholder='blur'
					blurDataURL={blurDataURL}
					priority
				/>
			</div>
		);
	}
};

// const CustomCodeHighlighter = ({ children, language, inline, ...props }: SyntaxHighlighterProps & { inline?: boolean }) => {
// 	const { resolvedTheme } = useTheme();
// 	const [currentTheme, setCurrentTheme] = useState<any>(atomOneDark);
// 	useEffect(
// 		() => resolvedTheme === 'light' ? setCurrentTheme(atomOneLight) : setCurrentTheme(atomOneDark),
// 		[resolvedTheme]
// 	);

// 	return !inline ? (
// 		<SyntaxHighlighter
// 			style={currentTheme}
// 			language={language}
// 			PreTag="div"
// 			customStyle={{
// 				padding: undefined
// 			}}
// 			className="text-md rounded-xl px-4 py-4 border border-grey-300 dark:border-grey-700 shadow-md shadow-grey-500 dark:shadow-grey-800"
// 			{...props}
// 		>
// 			{String(children).replace(/\n$/, '')}
// 		</SyntaxHighlighter>
// 	) : (
// 		<code className={`bg-indigo-100 dark:bg-slate-800 p-1 rounded-md`} {...props}>
// 			{children}
// 		</code>
// 	)
// };


const CustomCodeHighlighter = ({ children, language, inline, ...props }: SyntaxHighlighterProps & { inline?: boolean }) => {
	const { resolvedTheme } = useTheme();
	const [currentTheme, setCurrentTheme] = useState<any>(atomOneDark);
	const [copied, setCopied] = useState(false);

	useEffect(() => {
		resolvedTheme === 'light' ? setCurrentTheme(atomOneLight) : setCurrentTheme(atomOneDark);
	}, [resolvedTheme]);

	// Function to handle code copy
	const handleCopy = () => {
		navigator.clipboard.writeText(String(children).replace(/\n$/, ''));
		setCopied(true);
		setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
	};

	return !inline ? (
		<div className="relative">
			{/* Copy button */}
			<button
				onClick={handleCopy}
				className="absolute right-4 top-4 text-sm p-1 rounded-md bg-gray-200 dark:bg-gray-800 hover:bg-gray-300 dark:hover:bg-gray-700"
			>
				{copied ? <FiCheck className="text-green-500" /> : <FiCopy />}
			</button>
			<SyntaxHighlighter
				style={currentTheme}
				language={language}
				PreTag="div"
				customStyle={{
					padding: undefined
				}}
				className="text-md rounded-xl px-4 py-4 border border-grey-300 dark:border-grey-700 shadow-md shadow-grey-500 dark:shadow-grey-800"
				{...props}
			>
				{String(children).replace(/\n$/, '')}
			</SyntaxHighlighter>
		</div>
	) : (
		<code className="bg-indigo-100 dark:bg-slate-800 p-1 rounded-md" {...props}>
			{children}
		</code>
	);
};


const toBase64 = (str: string) =>
	typeof window === "undefined"
		? Buffer.from(str).toString("base64")
		: window.btoa(str);


const shimmer = (w = 1000, h = 600) => `
<svg width="${w}" height="${h}" xmlns="http://www.w3.org/2000/svg" version="1.1">
  <defs>
    <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="0%">
      <stop offset="0%" style="stop-color:#945bf1;stop-opacity:1" />
      <stop offset="0%" style="stop-color:#bb48bf;stop-opacity:1" >
        <animate 
          attributeName="offset" 
          from="0%"
          to="100%"
          dur="1s"
          repeatCount="indefinite"/>
      </stop>
      <stop offset="100%" style="stop-color:#945bf1;stop-opacity:1" />
    </linearGradient>
  </defs>
  <rect width="${w}" height="${h}" fill="url(#grad1)" />
</svg>
`

export const blurDataURL = `data:image/svg+xml;base64,${toBase64(shimmer())}`;
